import React, { createContext, useContext, useReducer, useEffect, useCallback, ReactNode } from 'react';
import { User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { AppContextState, AuthUser, UserPermission, AppSettings, LoadingState, ErrorState } from '../types';
import { onAuthStateChange, getUserPermissions } from '../services/auth';
import { db } from '../services/firebase';

// 액션 타입
type AppAction =
  | { type: 'SET_LOADING'; payload: Partial<LoadingState> }
  | { type: 'SET_USER'; payload: AuthUser | null }
  | { type: 'SET_PERMISSIONS'; payload: UserPermission | null }
  | { type: 'SET_SETTINGS'; payload: Partial<AppSettings> }
  | { type: 'SET_ERROR'; payload: ErrorState | null }
  | { type: 'CLEAR_ERROR' };

// 초기 상태
const initialState: AppContextState & { loading: LoadingState; error: ErrorState | null } = {
  user: null,
  loading: {
    users: false,
    upload: false,
    delete: false,
    auth: true
  },
  permissions: null,
  settings: {
    language: 'ko',
    theme: 'light'
  },
  error: null
};

// 리듀서
const appReducer = (
  state: typeof initialState,
  action: AppAction
): typeof initialState => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        loading: {
          ...state.loading,
          ...action.payload
        }
      };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        loading: {
          ...state.loading,
          auth: false
        }
      };
    case 'SET_PERMISSIONS':
      return {
        ...state,
        permissions: action.payload
      };
    case 'SET_SETTINGS':
      return {
        ...state,
        settings: {
          ...state.settings,
          ...action.payload
        }
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };
    default:
      return state;
  }
};

// Context 타입
interface AppContextType {
  state: typeof initialState;
  dispatch: React.Dispatch<AppAction>;
  setLoading: (loading: Partial<LoadingState>) => void;
  setUser: (user: AuthUser | null) => void;
  setPermissions: (permissions: UserPermission | null) => void;
  setSettings: (settings: Partial<AppSettings>) => void;
  setError: (error: ErrorState | null) => void;
  clearError: () => void;
  refreshPermissions: () => Promise<void>;
  isAdmin: boolean;
  canEdit: boolean;
}

// Context 생성
const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider 컴포넌트
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // 액션 헬퍼 함수들
  const setLoading = (loading: Partial<LoadingState>) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  };

  const setUser = (user: AuthUser | null) => {
    dispatch({ type: 'SET_USER', payload: user });
  };

  const setPermissions = (permissions: UserPermission | null) => {
    dispatch({ type: 'SET_PERMISSIONS', payload: permissions });
  };

  const setSettings = (settings: Partial<AppSettings>) => {
    dispatch({ type: 'SET_SETTINGS', payload: settings });
    // 로컬 스토리지에 저장
    localStorage.setItem('appSettings', JSON.stringify({
      ...state.settings,
      ...settings
    }));
  };

  const setError = (error: ErrorState | null) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // 권한 새로고침
  const refreshPermissions = useCallback(async () => {
    if (state.user?.email) {
      try {
        const permissions = await getUserPermissions(state.user.email);
        setPermissions(permissions);
      } catch (error) {
        console.error('권한 새로고침 실패:', error);
      }
    }
  }, [state.user?.email]);

  // 권한 체크 헬퍼
  const isAdmin = state.user?.email === process.env.REACT_APP_ADMIN_EMAIL || 
                  state.permissions?.level === 'admin';
  const canEdit = isAdmin || 
                  (state.permissions?.level === 'editor') || 
                  (state.permissions?.canEdit ?? false);

  // 인증 상태 변경 감지
  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (firebaseUser: User | null) => {
      if (firebaseUser) {
        const authUser: AuthUser = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL
        };
        
        setUser(authUser);

        // 권한 확인
        if (authUser.email) {
          try {
            const permissions = await getUserPermissions(authUser.email);
            setPermissions(permissions);
          } catch (error) {
            console.error('권한 확인 실패:', error);
            setPermissions(null);
          }
        }
      } else {
        setUser(null);
        setPermissions(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // 권한 실시간 리스너
  useEffect(() => {
    if (!state.user?.email) return;

    const permissionDocRef = doc(db, 'permissions', state.user.email);
    const unsubscribe = onSnapshot(
      permissionDocRef,
      (doc) => {
        if (doc.exists()) {
          const permissionData = doc.data() as UserPermission;
          setPermissions(permissionData);
          console.log('권한 실시간 업데이트:', permissionData);
        } else {
          // Firestore에 권한이 없으면 시스템 관리자 체크
          if (state.user && state.user.email === process.env.REACT_APP_ADMIN_EMAIL) {
            const systemAdminPermission: UserPermission = {
              email: state.user.email,
              level: 'admin',
              canEdit: true,
              grantedBy: 'system',
              grantedAt: new Date() as any
            };
            setPermissions(systemAdminPermission);
          } else {
            setPermissions(null);
          }
        }
      },
      (error) => {
        console.error('권한 리스너 오류:', error);
        // 권한 읽기 실패 시 null로 설정
        setPermissions(null);
      }
    );

    return () => unsubscribe();
  }, [state.user?.email]);

  // 로컬 스토리지에서 설정 로드
  useEffect(() => {
    try {
      const savedSettings = localStorage.getItem('appSettings');
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        dispatch({ type: 'SET_SETTINGS', payload: settings });
      }
    } catch (error) {
      console.error('설정 로드 실패:', error);
    }
  }, []);

  // 테마 적용
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', state.settings.theme);
  }, [state.settings.theme]);

  const contextValue: AppContextType = {
    state,
    dispatch,
    setLoading,
    setUser,
    setPermissions,
    setSettings,
    setError,
    clearError,
    refreshPermissions,
    isAdmin,
    canEdit
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// Hook for using context
export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

// Error boundary hook
export const useErrorHandler = () => {
  const { setError } = useApp();

  const handleError = useCallback((error: Error, context?: string) => {
    // Firebase 권한 오류인 경우 사용자 친화적 메시지로 변경
    let errorMessage = error.message;
    let errorCode = 'RUNTIME_ERROR';

    if (error.message.includes('Missing or insufficient permissions')) {
      errorMessage = '데이터에 접근할 권한이 없습니다. 관리자에게 문의하세요.';
      errorCode = 'PERMISSION_DENIED';
    } else if (error.message.includes('PERMISSION_DENIED')) {
      errorMessage = '권한이 부족합니다. 관리자에게 문의하세요.';
      errorCode = 'PERMISSION_DENIED';
    } else if (error.message.includes('UNAUTHENTICATED')) {
      errorMessage = '로그인이 필요합니다.';
      errorCode = 'UNAUTHENTICATED';
    } else if (error.message.includes('network')) {
      errorMessage = '네트워크 연결을 확인해주세요.';
      errorCode = 'NETWORK_ERROR';
    }

    setError({
      message: errorMessage,
      code: errorCode,
      timestamp: new Date()
    });
    
    // 개발 환경에서 콘솔에 로그 (원본 오류 메시지 포함)
    if (process.env.NODE_ENV === 'development') {
      console.error(`Error ${context ? `in ${context}` : ''}:`, error);
      console.error('Original error message:', error.message);
    }
  }, [setError]);

  return handleError;
};
